import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import { SITE_URL } from "@/lib/site";

const FAQS: { q: string; a: string }[] = [
  {
    q: "Apakah Notaku benar-benar gratis?",
    a: "Ya, 100% gratis tanpa biaya bulanan, tanpa iklan, dan tanpa login. Notaku dibangun untuk membantu UMKM Indonesia mencatat transaksi tanpa hambatan.",
  },
  {
    q: "Data nota saya disimpan di mana?",
    a: "Semua data (nota, pelanggan, preset, logo) tersimpan lokal di HP kamu memakai IndexedDB. Notaku tidak mengirim data ke server mana pun. Untuk aman, backup rutin ke file JSON dari menu Pengaturan.",
  },
  {
    q: "Kalau HP hilang atau ganti HP, apakah datanya hilang?",
    a: "Karena data tersimpan lokal, kalau HP hilang tanpa backup datanya ikut hilang. Solusinya: ekspor backup JSON secara berkala (Pengaturan → Backup) dan simpan di Google Drive atau email sendiri.",
  },
  {
    q: "Apakah Notaku bisa dipakai offline?",
    a: "Bisa. Notaku adalah PWA (Progressive Web App). Setelah dibuka sekali, Notaku bisa dipakai tanpa koneksi internet dan bisa dipasang ke layar utama seperti aplikasi biasa.",
  },
  {
    q: "Bagaimana cara memasang Notaku di layar utama?",
    a: "Di Android/Chrome: buka menu (⋮) → 'Tambahkan ke Layar Utama'. Di iPhone/Safari: ketuk tombol Share → 'Add to Home Screen'.",
  },
  {
    q: "Bisa kirim nota ke pelanggan lewat WhatsApp?",
    a: "Bisa. Setelah nota disimpan, kamu bisa bagikan sebagai gambar atau teks langsung ke nomor WhatsApp pelanggan dalam satu tap.",
  },
  {
    q: "Apakah bisa mencetak struk ke printer thermal Bluetooth?",
    a: "Saat ini Notaku fokus ke berbagi via WhatsApp dan simpan struk sebagai gambar. Dukungan printer Bluetooth ESC/POS sedang dalam rencana pengembangan.",
  },
  {
    q: "Berapa maksimal nota yang bisa disimpan?",
    a: "Tidak ada batas ketat. IndexedDB umumnya menampung ratusan MB. Praktis, kamu bisa simpan puluhan ribu nota tanpa masalah performa.",
  },
  {
    q: "Bagaimana cara menghitung laba?",
    a: "Isi kolom 'Modal' (harga pokok) di setiap preset item. Notaku akan otomatis menghitung laba = total penjualan − total modal untuk setiap nota.",
  },
];

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "FAQ — Pertanyaan Umum Notaku Aplikasi Nota UMKM" },
      { name: "description", content: "Jawaban lengkap seputar Notaku: keamanan data, offline, backup, WhatsApp, printer, dan cara pakai untuk UMKM Indonesia." },
      { property: "og:title", content: "FAQ Notaku — Pertanyaan Umum Aplikasi Nota UMKM" },
      { property: "og:description", content: "Jawaban lengkap seputar Notaku: keamanan data, offline, backup, WhatsApp, printer." },
      { property: "og:url", content: `${SITE_URL}/faq` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/faq` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: FAQPage,
});

function FAQPage() {
  return (
    <div className="space-y-4">
      <Link to="/" className="tap inline-flex items-center gap-1 text-sm text-muted-foreground">
        <ChevronLeft className="h-4 w-4" /> Beranda
      </Link>
      <header>
        <h1 className="text-2xl font-display font-semibold tracking-tight">Pertanyaan Umum</h1>
        <p className="t-caption mt-1">Semua yang perlu kamu tahu tentang Notaku.</p>
      </header>
      <ul className="space-y-2">
        {FAQS.map((f, i) => (
          <li key={i}>
            <details className="rounded-2xl border border-border bg-card p-4 shadow-soft group">
              <summary className="cursor-pointer list-none font-medium flex items-start justify-between gap-3">
                <span>{f.q}</span>
                <span className="text-muted-foreground text-lg leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.a}</p>
            </details>
          </li>
        ))}
      </ul>
    </div>
  );
}
